import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import HeaderCard from "../../components/RetailerBusinessAgent/HeaderCard";
import ReferralLinkCard from "../../components/RetailerBusinessAgent/ReferralLinkCard";
import StatusGrid from "../../components/RetailerBusinessAgent/StatusGrid";
import SideBar from "../../components/RetailerBusinessAgent/SideBar";
import "./ReferBusiness.css";
import logo from "../../images/site_logo/TC-Logo-icon.png";
import dollarY from "../../images/shapes/Dollar Bag (1).png";
import business from "../../images/shapes/Business.png";
import building from "../../images/shapes/Business Building.png";
import WelcomeModal from "../../components/RetailerBusinessAgent/WelcomeModal";
import { API_URL } from "../../constant";

const ReferralPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [user, setUser] = useState(null);
  const [containerStyle, setContainerStyle] = useState({});

  // Set container style based on user role
  const updateContainerStyle = (role) => {
    let background = "";

    switch (role) {
      case "SeniorBusinessAdsAgent":
        background = "linear-gradient(to bottom, #024225, white)";
        break;
      case "MiniBusinessAdsAgent":
        background = "linear-gradient(to bottom, #09450b, white)";
        break;
      default:
        background = "linear-gradient(to bottom, #024225, white)";
        break;
    }

    setContainerStyle({
      display: "flex",
      flexDirection: "column",
      marginTop: "0px",
      marginBottom: "0px",
      background: background,
      width: "100%",
      minHeight: "100vh",
      fontFamily: "'Poppins', sans-serif",
      padding: "20px",
    });
  };

  // Check if modal has been shown before for this user
  const hasModalBeenShown = () => {
    const userId = user?.id || user?.email;
    if (!userId) return false;

    const shownModals = JSON.parse(
      localStorage.getItem("shown_welcome_modals") || "{}"
    );
    return shownModals[userId] === true;
  };

  // Mark modal as shown for this user
  const markModalAsShown = () => {
    const userId = user?.id || user?.email;
    if (!userId) return;

    const shownModals = JSON.parse(
      localStorage.getItem("shown_welcome_modals") || "{}"
    );
    shownModals[userId] = true;
    localStorage.setItem("shown_welcome_modals", JSON.stringify(shownModals));
  };

  useEffect(() => {
    fetchAgentData();
  }, [location.pathname]);

  useEffect(() => {
    // Function to check for token and navigate
    const checkAuthAndNavigate = () => {
      const token = localStorage.getItem("auth_token");
      const tempTokenKey = new URLSearchParams(window.location.search).get(
        "temp_token_key"
      );

      // Try to get token from tempTokenKey first
      if (tempTokenKey) {
        const tempToken = localStorage.getItem(tempTokenKey);
        if (tempToken) {
          localStorage.setItem("auth_token", tempToken);
          localStorage.setItem("user_type", "agent");
          localStorage.removeItem(tempTokenKey);

          // Check status and navigate if approved
          checkStatusAndNavigate(tempToken);
          return;
        }
      }

      // Use existing token if available
      if (token) {
        checkStatusAndNavigate(token);
        return;
      }

      // No token found, redirect to login
      navigate("/retail-agent-login");
    };

    const checkStatusAndNavigate = async (token) => {
      try {
        const response = await fetch(`${API_URL}/api/agent/status`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          const status = data.user_status;

          // Only allow approved/active status
          if (status === "active" || status === "approved") {
            // We're already on the referral page, so just fetch data
            fetchAgentData(token);
          } else {
            // Handle other statuses
            handleNonApprovedStatus(status, token);
          }
        }
      } catch (error) {
        console.error("Error checking status:", error);
        navigate("/retail-agent-login");
      }
    };

    const handleNonApprovedStatus = (status, token) => {
      // Close this tab if not approved
      switch (status) {
        case "awaiting":
        case "pending":
          alert("Your profile is under review. Please wait for approval.");
          window.close();
          break;
        case "suspended":
        case "rejected":
          alert(`Account ${status}. Please contact support.`);
          window.close();
          break;
        default:
          window.close();
          break;
      }
    };

    // Message listener for communication from login tab
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "LOGIN_SUCCESS") {
        localStorage.setItem("auth_token", event.data.token);
        localStorage.setItem("user_type", "agent");

        if (event.data.tempTokenKey) {
          localStorage.removeItem(event.data.tempTokenKey);
        }

        if (event.data.shouldNavigate) {
          // Already on the right page, just refresh data
          checkStatusAndNavigate(event.data.token);
        }
      }
    };

    // Add message listener
    window.addEventListener("message", handleMessage);

    // Initial check
    checkAuthAndNavigate();

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate]);

  const fetchAgentData = async (providedToken = null) => {
    const token = providedToken || localStorage.getItem("auth_token");
    if (!token) {
      navigate("/retail-agent-login");
      return;
    }

    setIsLoading(true);
    try {
      // 1. Get user profile data
      const userResponse = await fetch(`${API_URL}/api/agent/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!userResponse.ok) {
        throw new Error("Failed to fetch user data");
      }

      const userData = await userResponse.json();
      setUser(userData.user);

      // Update container style based on user role
      updateContainerStyle(userData.user.role);

      // Check if modal should be shown for this user
      const userId = userData.user?.id || userData.user?.email;
      if (userId) {
        const shownModals = JSON.parse(
          localStorage.getItem("shown_welcome_modals") || "{}"
        );
        if (!shownModals[userId]) {
          setShowModal(true);
        }
      }

      // 2. Get referral dashboard data
      const dashboardResponse = await fetch(
        `${API_URL}/api/agent/referral-dashboard`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!dashboardResponse.ok) {
        throw new Error("Failed to fetch referral data");
      }

      const dashboardData = await dashboardResponse.json();

      // Transform API response to match your frontend structure
      const transformedData = transformApiData(
        userData.user,
        dashboardData.data
      );
      setReferralData(transformedData);
    } catch (error) {
      console.error("Error fetching agent data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Transform API response to match your frontend structure
  const transformApiData = (userData, dashboardData) => {
    return {
      user: {
        firstName: userData.first_name,
        sureName: userData.last_name,
        fullName: `${userData.first_name} ${userData.last_name}`,
        role:
          userData.role === "SeniorBusinessAdsAgent"
            ? "Senior Business Ads Agent"
            : "Mini Business Ads Agent",
        referralLink:
          dashboardData.referral_links?.business_referral_link ||
          `${window.location.origin}/retail-business-signup?ref=${userData.referral_code}`,
        avatar: userData.photo_url || "",
        email: userData.email,
        phone: userData.phone,
        id: userData.id,
      },
      counts: {
        miniBusinessAdsAgents:
          dashboardData.total_counts?.total_agents_referred || 0,
        retailBusinesses:
          dashboardData.total_counts?.total_businesses_referred || 0,
      },
      earnings: {
        topcoinValue: dashboardData.points_summary?.total_points_earned || 0,
        topcoinGBP:
          parseFloat(dashboardData.points_summary?.total_points_earned || 0) /
          100,
        earnedBonus: dashboardData.points_summary?.senior_agent_bonus || 0,
      },
      dashboardData: dashboardData,
    };
  };

  const handleCloseModal = () => {
    setShowModal(false);
    markModalAsShown();
  };

  const handleCopyLink = async () => {
    if (referralData?.user?.referralLink) {
      try {
        await navigator.clipboard.writeText(referralData.user.referralLink);
        alert("Referral link copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    }
  };

  const handleShareLink = () => {
    if (!referralData?.user?.referralLink) {
      alert("Referral link not available");
      return;
    }

    const referralLink = referralData.user.referralLink;

    // Formatted share message for customers
    const shareMessage = `Topcoin UK is the Next Generation Loyalty Platform.

Join the UK’s Smartest Rewards Evolution.

I’m pleased to inform you that I’ve recently joined TopCoin UK, the UK’s smartest and most innovative rewards and customer loyalty evolution.

TopCoin UK helps businesses attract new customers, increase footfall, and keep loyal customers returning, all through a powerful, modern rewards system.

If you feel this may benefit your business, I would be delighted to refer you. You can explore the platform and register using my referral link below:

${referralLink}

There is no obligation. I simply wanted to share a solution that may help your business grow while rewarding your customers at the same time.`;

    if (navigator.share) {
      navigator
        .share({
          title: "Join TopCoin UK - The Next Generation Loyalty Platform",
          text: shareMessage,
        })
        .catch((err) => {
          console.error("Error sharing:", err);
          // Fallback to clipboard if share fails
          navigator.clipboard
            .writeText(shareMessage)
            .then(() =>
              alert(
                "Share message copied to clipboard! You can now paste it in any messaging app."
              )
            )
            .catch((err) => console.error("Failed to copy:", err));
        });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard
        .writeText(shareMessage)
        .then(() =>
          alert(
            "Share message copied to clipboard! You can now paste it in any messaging app."
          )
        )
        .catch((err) => {
          console.error("Failed to copy:", err);
          // Ultimate fallback - show the message in an alert
          alert(`Share this message:\n\n${shareMessage}`);
        });
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ marginTop: "20px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ color: "red" }}>Error: {error}</div>
      </div>
    );
  }

  if (!referralData || !user) {
    return null;
  }

  const totalEarning = (
    referralData.earnings.topcoinGBP + referralData.earnings.earnedBonus
  ).toFixed(2);

  const isMiniAgent = referralData.user.role === "Mini Business Ads Agent";

  const statusItems = [];

  // First card (only for non-mini agents)
  if (!isMiniAgent) {
  statusItems.push({
    img: building,
    value: referralData.counts.miniBusinessAdsAgents,
    label: "Mini Business Ads Agents",
    bgColor: "#f0fdf4",
    bgColor1: "#dcfce7",
    onClick: () => navigate("/retailer-business-agent/referral-history?tab=agents") // Add navigation
  });
}

  // Second card
  statusItems.push({
  img: business,
  value: referralData.counts.retailBusinesses,
  label: "Retail Business",
  bgColor: "#eff6ff",
  bgColor1: "#dbeafe",
  onClick: () => navigate("/retailer-business-agent/referral-history?tab=businesses") // Add navigation
});

  // Third card
  statusItems.push({
    img: dollarY,
    value: `£ ${totalEarning}`,
    label: "Total Rewards",
    bgColor: "#fffbeb",
    bgColor1: "#fef3c7",
  });

  return (
    <div style={containerStyle}>
      <div className="refer-container1">
        <HeaderCard
          logo={logo}
          title="TopCoin UK"
          subtitle="CREATE RETAIL BUSINESS INVITATION LINK"
          description="Invite retail businesses and receive TopCoin rewards when they join and actively participate."
        />

        <ReferralLinkCard
          link={referralData.user.referralLink}
          onCopy={handleCopyLink}
          onShare={handleShareLink}
        />

        <StatusGrid items={statusItems} />
      </div>

      {showModal && (
        <WelcomeModal user={referralData.user} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default ReferralPage;
